import { cn } from '@nextui-org/react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import FileList from '~/components/editors/monaco/FileList'
import DiffReviewPanel from '~/components/task/DiffReviewPanel'
import PullRequestResult from '~/components/task/PullRequestResult'
import { useConversationUuid } from '~/hooks'
import { AgentTaskStatus, CommandType, Diff } from '~/protos/agents'
import { applyDiff } from '~/services/taskService'
import { TabOption } from '~/types/TabOption'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { BetterToast } from '~/utils/toast'

interface CodeEditorProps {
  diffs: Diff[] | null
  readOnly?: boolean
  /** Show approve button - controlled by container */
  showApproveButton?: boolean
  /** Show feedback controls (textarea + button) - controlled by container */
  showFeedbackControls?: boolean
  commandType: CommandType
  taskId?: number
  taskStatus?: AgentTaskStatus
  tupleIndex?: number
  onDownloadOriginalRepo?: () => void
  /** Whether approval is in progress (persists across tab switches) */
  isApproving?: boolean
  /** Callback to update approval state */
  onApprovingChange?: (isApproving: boolean) => void
  /** Callback when approval starts (for immediate navigation) */
  onApproveStart?: () => void
  /** Project name resolved by the parent surface when TaskContext is not available. */
  projectName?: string
  prLink?: string
}

/**
 * Presentational component for code review - action controls + shared diff viewer.
 * All visibility logic is controlled by parent via props.
 */
function CodeEditor({
  diffs: propDiffs,
  readOnly = false,
  showApproveButton = false,
  showFeedbackControls = false,
  commandType,
  taskId,
  taskStatus,
  tupleIndex,
  onDownloadOriginalRepo,
  isApproving = false,
  onApprovingChange,
  onApproveStart,
  projectName,
  prLink
}: CodeEditorProps): JSX.Element {
  const uuid = useConversationUuid()
  const { workspaceUuid } = useParams<{ workspaceUuid: string }>()
  const [diffs, setDiffs] = useState<Diff[] | null>(propDiffs ?? null)
  const [activeDiff, setActiveDiff] = useState<Diff | null>(propDiffs?.[0] ?? null)

  // Sync when propDiffs changes (data loads asynchronously)
  useEffect(() => {
    if (propDiffs && propDiffs.length > 0) {
      setDiffs(propDiffs)
      setActiveDiff(prev => {
        if (!prev) return propDiffs[0]
        const stillExists = propDiffs.find(d => d.bFilePath === prev.bFilePath)
        return stillExists ?? propDiffs[0]
      })
      return
    }

    setDiffs(propDiffs ?? null)
    setActiveDiff(null)
  }, [propDiffs])

  const onAcceptDiff = async () => {
    if (!taskId) {
      BetterToast.error('Task ID not available')
      return
    }

    if (!workspaceUuid) {
      BetterToast.error('Workspace not found')
      return
    }

    if (Array.isArray(diffs)) {
      // Immediately update state and navigate before async call
      onApprovingChange?.(true)
      onApproveStart?.()

      try {
        await applyDiff({ workspaceUuid, uuid, taskId, diffs, commandType })
        BetterToast.success('Diff applied')
        globalEventEmitter.emit(AppEvents.setActiveTab, TabOption.HELP)
      } catch (error) {
        console.error('Error applying diff:', error)
        BetterToast.error('Failed to apply diff')
        onApprovingChange?.(false) // Only reset on error, success will change task status
      }
    }
  }

  return (
    <div
      className={cn('flex h-full min-h-0 w-full flex-col overflow-hidden pb-6')}
      data-testid='code-editor-review-surface'
    >
      <div className='shrink-0'>
        <FileList
          diffs={diffs}
          activeDiff={activeDiff}
          onSelectDiff={setActiveDiff}
          onAcceptDiff={onAcceptDiff}
          readOnly={readOnly}
          showApproveButton={showApproveButton}
          showFeedbackControls={showFeedbackControls}
          commandType={commandType}
          taskStatus={taskStatus}
          tupleIndex={tupleIndex}
          onDownloadOriginalRepo={onDownloadOriginalRepo}
          isApproving={isApproving}
          projectName={projectName}
          taskId={taskId}
          showFileSelector={false}
        />
      </div>
      <div
        className='mt-4 min-h-0 w-full flex-1 overflow-hidden'
        data-testid='code-editor-diff-frame'
      >
        <div className='flex h-full min-h-0 flex-col gap-3'>
          {prLink ? <PullRequestResult prLink={prLink} className='shrink-0' /> : null}
          {diffs?.length ? (
            <div className='min-h-0 flex-1 overflow-hidden'>
              <DiffReviewPanel diffs={diffs} fillHeight />
            </div>
          ) : (
            <div className='flex min-h-0 flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white text-sm text-tokens-neutral-text-tertiary'>
              No diff available
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CodeEditor
